import { DropdownItem } from '@vinnova/ui';
import {
  ExcelImportRowAssignments,
  ExcelImportSourceRow,
} from './excel-import-modal.model';

/** Slot value for a file row the user does not want to import. */
export const EXCEL_IMPORT_SLOT_NONE = 'none';

/** One amount from one source cell, as the analysis reported it. */
export interface ExcelImportPlaceableCell {
  ar: number;
  summa: number;
  /** Slot the analysis suggested; empty when it had no suggestion. */
  slot: string;
  /** Identifies the source row across years; cells without one each count as their own row. */
  rowKey?: string;
  label?: string;
  sourceSheet?: string;
  sourceCell?: string;
}

/** Amounts of all rows placed in the same slot and year, added up. */
export interface ExcelImportPlacedCell {
  slot: string;
  ar: number;
  summa: number;
  source?: string;
}

export interface ExcelImportPlacement {
  sourceRows: ExcelImportSourceRow[];
  placedCells: ExcelImportPlacedCell[];
}

interface SourceRow {
  key: string;
  label: string;
  source?: string;
  cells: ExcelImportPlaceableCell[];
  suggestedSlot: string;
}

interface RowPlacement {
  slot: string;
  withheldSuggestionLabel?: string;
}

/**
 * Turns the analysis' per-cell suggestions into rows the user can place. Starts from the suggestions, but when several
 * rows suggest the same slot none of them is placed: combining rows is the user's decision, never made for them.
 * Rows the user places in the same slot are added up here, the only place amounts are summed.
 */
export function placeExcelImportRows(
  partId: string,
  cells: ExcelImportPlaceableCell[],
  slotOptions: DropdownItem[],
  assignments: ExcelImportRowAssignments,
  years: number[],
  scale: (summa: number) => number,
): ExcelImportPlacement {
  const rows = groupRows(partId, cells);
  const placements = placeRows(rows, assignments, slotOptions);

  return {
    sourceRows: rows.map((row) => {
      const placement = placements.get(row.key) ?? {
        slot: EXCEL_IMPORT_SLOT_NONE,
      };
      return {
        key: row.key,
        label: row.label,
        source: row.source,
        amounts: Object.fromEntries(
          years.map((ar) => {
            const inYear = row.cells.filter((c) => c.ar === ar);
            return [
              ar,
              inYear.length > 0
                ? scale(inYear.reduce((sum, c) => sum + c.summa, 0))
                : undefined,
            ];
          }),
        ),
        slot: placement.slot,
        withheldSuggestionLabel: placement.withheldSuggestionLabel,
        sharesSlotWith:
          placement.slot === EXCEL_IMPORT_SLOT_NONE
            ? 0
            : [...placements.values()].filter((p) => p.slot === placement.slot)
                .length - 1,
      };
    }),
    placedCells: sumPlacedCells(rows, placements),
  };
}

function groupRows(
  partId: string,
  cells: ExcelImportPlaceableCell[],
): SourceRow[] {
  const rows = new Map<string, SourceRow>();
  for (const [index, cell] of cells.entries()) {
    const key = partId + '|' + (cell.rowKey ?? 'cell-' + index);
    let row = rows.get(key);
    if (!row) {
      row = {
        key,
        label: cell.label ?? cell.sourceCell ?? '',
        cells: [],
        suggestedSlot: '',
      };
      rows.set(key, row);
    }
    row.cells.push(cell);
    row.suggestedSlot ||= cell.slot;
  }

  return [...rows.values()].map((row) => ({
    ...row,
    source: describeSources(row.cells),
  }));
}

function placeRows(
  rows: SourceRow[],
  assignments: ExcelImportRowAssignments,
  slotOptions: DropdownItem[],
): Map<string, RowPlacement> {
  const labels = new Map(slotOptions.map((o) => [o.value, o.label]));
  const suggestionCount = new Map<string, number>();
  for (const row of rows) {
    if (row.suggestedSlot) {
      suggestionCount.set(
        row.suggestedSlot,
        (suggestionCount.get(row.suggestedSlot) ?? 0) + 1,
      );
    }
  }

  return new Map(
    rows.map((row): [string, RowPlacement] => {
      const chosen = assignments[row.key];
      if (chosen !== undefined && labels.has(chosen)) {
        return [row.key, { slot: chosen }];
      }
      const suggested = labels.has(row.suggestedSlot) ? row.suggestedSlot : '';
      if (suggested && suggestionCount.get(suggested) === 1) {
        return [row.key, { slot: suggested }];
      }
      return [
        row.key,
        {
          slot: EXCEL_IMPORT_SLOT_NONE,
          withheldSuggestionLabel: suggested
            ? labels.get(suggested)
            : undefined,
        },
      ];
    }),
  );
}

function sumPlacedCells(
  rows: SourceRow[],
  placements: Map<string, RowPlacement>,
): ExcelImportPlacedCell[] {
  const groups = new Map<
    string,
    { slot: string; ar: number; cells: ExcelImportPlaceableCell[] }
  >();
  for (const row of rows) {
    const slot = placements.get(row.key)?.slot ?? EXCEL_IMPORT_SLOT_NONE;
    if (slot === EXCEL_IMPORT_SLOT_NONE) {
      continue;
    }
    for (const cell of row.cells) {
      const key = slot + '|' + cell.ar;
      const group = groups.get(key) ?? { slot, ar: cell.ar, cells: [] };
      group.cells.push(cell);
      groups.set(key, group);
    }
  }

  return [...groups.values()].map((group) => ({
    slot: group.slot,
    ar: group.ar,
    summa: group.cells.reduce((sum, c) => sum + c.summa, 0),
    source: describeSources(group.cells, ' + '),
  }));
}

/** "Sheet!B5, C5" (or "Sheet!B5 + C5" with another separator); sheets are named once, in order of appearance. */
function describeSources(
  cells: ExcelImportPlaceableCell[],
  separator = ', ',
): string | undefined {
  const bySheet = new Map<string, string[]>();
  for (const cell of cells) {
    if (cell.sourceCell) {
      const refs = bySheet.get(cell.sourceSheet ?? '') ?? [];
      refs.push(cell.sourceCell);
      bySheet.set(cell.sourceSheet ?? '', refs);
    }
  }
  const parts = [...bySheet.entries()].map(
    ([sheet, refs]) => (sheet ? sheet + '!' : '') + refs.join(separator),
  );
  return parts.length > 0 ? parts.join(' + ') : undefined;
}
